// The panel of each « Mise en route » step (see Onboarding.js). Facts come from
// the backend step (step.facts); writes go through the existing endpoints
// (carriers, B2B clients) or the onboarding decisions, then the page reloads
// the status. French defaults here, translations under onboarding.*.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Bot, Boxes, Check, CircleCheck, CircleDashed, ClipboardList, Database, Mail, MessageCircle,
  MessagesSquare, Phone, Plug, Plus, ShoppingCart, Store, Truck, Warehouse,
} from 'lucide-react';
import { Badge, Banner, Field, IconChip } from '../../design/ui';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

export const STEP_TEXT = {
  profile: ['Votre activité', 'Les étapes suivantes s’adaptent à ce que vous faites.'],
  channels: ['Canaux de communication', 'Par où vos clients vous écrivent. Un seul canal suffit pour commencer ; ajoutez les autres quand vous voulez.'],
  clients: ['Clients B2B', 'Les entreprises pour lesquelles vous expédiez. Chaque ticket leur est rattaché d’après l’adresse de l’expéditeur, et leurs consignes s’appliquent.'],
  carriers: ['Transporteurs', 'Ceux qui livrent vos colis : l’agent suit chaque colis et contacte le transporteur quand il faut agir.'],
  data_sources: ['Sources de données', 'Votre ERP, votre WMS, votre TMS : l’agent répond avec la vraie commande et le vrai colis, sans demander de copier-coller.'],
  shop: ['Boutique et commandes', 'D’où viennent vos commandes : l’agent retrouve la commande du client et son colis.'],
  agent: ['Agent IA', 'Celui qui répond à vos clients. Vérifiez son nom, son ton, sa signature et ce qu’il peut promettre.'],
  go_live: ['Mise en service', 'Comment les réponses partent au début. Vous pourrez changer à tout moment.'],
};

// A connection row: chip · title + state badge · one line of context · the
// action useful now (connect / set up) or « Gérer ».
const Tile = ({ icon, title, detail, on, route, t, onText, actionText }) => (
  <li className="u-row">
    <IconChip icon={icon} tone={on ? 'success' : 'neutral'} size="sm" />
    <div className="u-row__main">
      <div className="u-row__title">
        {title}
        <Badge tone={on ? 'success' : undefined} dot={on}>
          {on ? (onText || t('onboarding.connected', 'Connecté')) : t('onboarding.notConnected', 'Non connecté')}
        </Badge>
      </div>
      {detail && <div className="u-row__meta u-row__meta--wrap">{detail}</div>}
    </div>
    {route && (
      <div className="u-row__side">
        <Link className={`u-btn u-btn--sm ${on ? 'u-btn--ghost' : 'u-btn--secondary'}`} to={route}>
          {actionText || (on ? t('onboarding.manage', 'Gérer') : t('onboarding.connect', 'Connecter'))}
          <ArrowRight aria-hidden="true" />
        </Link>
      </div>
    )}
  </li>
);

const Rows = ({ children }) => <ul className="u-list onboarding-v2__rows">{children}</ul>;

const Hint = ({ children }) => <p className="onboarding-v2__hint">{children}</p>;

const Actions = ({ children }) => <div className="onboarding-v2__actions">{children}</div>;

// --- 1. Profile ------------------------------------------------------------------------

const PROFILES = [
  { key: 'logistician', icon: Warehouse, title: 'Logisticien / 3PL', text: 'Vous stockez et expédiez pour des marques, vos clients B2B. Les demandes viennent d’eux et de leurs propres clients.' },
  { key: 'merchant', icon: Store, title: 'E-commerçant', text: 'Vous vendez en ligne à des particuliers. Les demandes viennent de vos clients, sur leurs commandes et leurs colis.' },
];

const Choice = ({ chosen, disabled, onClick, icon, title, text, badge, compact }) => (
  <button
    type="button"
    role="radio"
    aria-checked={chosen}
    disabled={disabled}
    className={`onboarding-v2__choice${chosen ? ' is-chosen' : ''}${compact ? ' is-compact' : ''}`}
    onClick={onClick}
  >
    {icon && <IconChip icon={icon} tone={chosen ? undefined : 'neutral'} />}
    <span className="onboarding-v2__choice-text">
      <strong>{title} {badge}</strong>
      <span>{text}</span>
    </span>
    <span className="onboarding-v2__choice-check" aria-hidden="true">{chosen && <Check strokeWidth={2.4} />}</span>
  </button>
);

const ProfileStep = ({ status, actions, t }) => (
  <div className="onboarding-v2__choices" role="radiogroup" aria-label={t('onboarding.steps.profile.title', 'Votre activité')}>
    {PROFILES.map(({ key, icon, title, text }) => {
      const chosen = status.profile === key;
      return (
        <Choice
          key={key}
          chosen={chosen}
          disabled={actions.busy}
          onClick={() => !chosen && actions.setProfile(key)}
          icon={icon}
          title={t(`onboarding.profile.${key}.title`, title)}
          text={t(`onboarding.profile.${key}.text`, text)}
        />
      );
    })}
  </div>
);

// --- 2. Channels -----------------------------------------------------------------------

const ChannelsStep = ({ step, status, t }) => {
  const f = step.facts || {};
  const mailbox = f.gmail || f.outlook > 0;
  const mailDetail = [
    f.gmail ? 'Gmail' : null,
    f.outlook ? t('onboarding.channels.outlookCount', '{{n}} boîte(s) Outlook', { n: f.outlook }) : null,
  ].filter(Boolean).join(' · ') || t('onboarding.channels.emailHint', 'Gmail ou Outlook : l’essentiel des demandes arrive par là.');
  const isLogistician = status.profile !== 'merchant';
  return (
    <>
      {f.outlook_reconnect > 0 && (
        <Banner
          tone="warning"
          actions={<Link className="u-btn u-btn--secondary u-btn--sm" to="/parameters?tab=integrations">{t('teamStatsUi.onboarding.reconnect', 'Reconnecter')}</Link>}
        >
          {t('onboarding.channels.reconnect', '{{n}} boîte(s) Outlook à reconnecter : leurs e-mails n’arrivent plus.', { n: f.outlook_reconnect })}
        </Banner>
      )}
      <Rows>
        <Tile t={t} icon={Mail} title={t('onboarding.channels.email', 'E-mail')} detail={mailDetail} on={mailbox} route="/parameters?tab=integrations" />
        <Tile
          t={t} icon={MessagesSquare} title={t('onboarding.channels.chatbot', 'Chatbot de votre site')}
          detail={t('onboarding.channels.chatbotHint', 'Une bulle de discussion sur votre site, qui répond tout de suite.')}
          on={f.chatbot} route="/chatbot-settings" actionText={f.chatbot ? null : t('onboarding.setUp', 'Mettre en place')}
        />
        <Tile
          t={t} icon={ClipboardList} title={t('onboarding.channels.webForm', 'Formulaire de contact')}
          detail={t('onboarding.channels.webFormHint', 'Un formulaire qui demande d’emblée le n° de commande ou de suivi.')}
          on={f.web_form} route="/ticket-form-settings" actionText={f.web_form ? null : t('onboarding.setUp', 'Mettre en place')}
        />
        <Tile
          t={t} icon={MessageCircle} title={t('onboarding.channels.messaging', 'WhatsApp, Messenger, Instagram')}
          detail={(f.messaging || []).length ? f.messaging.map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(', ') : null}
          on={(f.messaging || []).length > 0} route="/parameters?tab=integrations"
        />
        <Tile
          t={t} icon={Phone} title={t('onboarding.channels.voice', 'Téléphone (agent vocal)')}
          detail={f.voice ? t('onboarding.channels.voiceCount', '{{n}} numéro(s)', { n: f.voice }) : t('onboarding.channels.voiceHint', 'Un numéro auquel l’agent vocal répond.')}
          on={f.voice > 0} route="/voice-numbers" actionText={f.voice > 0 ? null : t('onboarding.setUp', 'Mettre en place')}
        />
        {isLogistician && (
          <Tile
            t={t} icon={Plug} title={t('onboarding.channels.erpApi', 'API pour vos clients B2B')}
            detail={f.erp_api ? t('onboarding.channels.erpCount', '{{n}} client(s) envoient leurs tickets par l’API', { n: f.erp_api }) : t('onboarding.channels.erpHint', 'Leur ERP crée les tickets directement : une clé par client, sur sa fiche.')}
            on={f.erp_api > 0} route="/clients-instructions" actionText={f.erp_api > 0 ? null : t('onboarding.setUp', 'Mettre en place')}
          />
        )}
        {f.edn && <Tile t={t} icon={Truck} title="Envoi du Net" detail={t('onboarding.channels.ednHint', 'Les tickets de votre compte Envoi du Net arrivent seuls.')} on />}
        {f.spacefill && <Tile t={t} icon={Warehouse} title="Spacefill" detail={t('onboarding.channels.spacefillHint', 'Chaque incident Spacefill devient un ticket.')} on route="/connectors" />}
      </Rows>
    </>
  );
};

// --- 3a. B2B clients (logistician) -----------------------------------------------------

const ADDRESS_RE = /^(?:[^\s@]+@)?(?:[a-z0-9-]+\.)+[a-z]{2,}$/i;

const ClientsStep = ({ step, actions, t }) => {
  const f = step.facts || {};
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [addresses, setAddresses] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/logistics/clients-edn', authHeaders())
      .then((res) => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [f.total]);

  const add = async (event) => {
    event.preventDefault();
    const list = addresses.split(/[\s,;]+/).map((a) => a.trim().replace(/^@/, '')).filter(Boolean);
    const bad = list.find((a) => !ADDRESS_RE.test(a));
    if (!name.trim()) return setError(t('onboarding.clients.nameRequired', 'Le nom du client est requis.'));
    if (bad) return setError(t('onboarding.clients.badAddress', '« {{value}} » n’est ni une adresse e-mail ni un domaine.', { value: bad }));
    setSaving(true);
    setError('');
    try {
      await axios.post('/api/logistics/clients-edn', { name: name.trim(), emails: list }, authHeaders());
      setName('');
      setAddresses('');
      await actions.refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('onboarding.clients.saveError', 'Le client n’a pas pu être ajouté.'));
    } finally {
      setSaving(false);
    }
    return null;
  };

  return (
    <>
      <form className="onboarding-v2__quickadd" onSubmit={add}>
        <Field label={t('onboarding.clients.name', 'Nom du client')} htmlFor="onboarding-client-name">
          <input id="onboarding-client-name" className="u-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder={t('onboarding.clients.namePlaceholder', 'ex. Maison Dupont')} />
        </Field>
        <Field label={t('onboarding.clients.addresses', 'Adresses ou domaines e-mail')} htmlFor="onboarding-client-addresses">
          <input id="onboarding-client-addresses" className="u-input" value={addresses} onChange={(e) => setAddresses(e.target.value)} placeholder={t('onboarding.clients.addressesPlaceholder', 'ex. maisondupont.fr, sav@maisondupont.fr')} />
        </Field>
        <button type="submit" className="u-btn u-btn--primary" disabled={saving || actions.busy}>
          <Plus aria-hidden="true" /> {t('onboarding.clients.add', 'Ajouter')}
        </button>
      </form>
      <Hint>{t('onboarding.clients.hint', 'Un domaine reconnaît toutes les adresses de l’entreprise. Les consignes, transporteurs autorisés et accès API se règlent ensuite sur la fiche du client.')}</Hint>
      {error && <Banner tone="danger">{error}</Banner>}
      {rows.length > 0 && (
        <Rows>
          {rows.slice(0, 8).map((row) => (
            <li className="u-row" key={row.id || row.name}>
              <div className="u-row__main">
                <div className="u-row__title">{row.name}</div>
                <div className="u-row__meta">
                  {(row.emails || []).length ? row.emails.join(', ') : t('onboarding.clients.noAddress', 'aucune adresse : pas encore reconnaissable')}
                </div>
              </div>
              {!(row.emails || []).length && <Badge tone="warning">{t('teamStatsUi.onboarding.noAddressBadge', 'Sans adresse')}</Badge>}
            </li>
          ))}
          {rows.length > 8 && <li className="u-row onboarding-v2__more">{t('onboarding.clients.more', 'et {{n}} autre(s)', { n: rows.length - 8 })}</li>}
        </Rows>
      )}
      <Actions>
        <Link className="u-btn u-btn--ghost" to="/clients-instructions">
          {t('onboarding.clients.manage', 'Gérer les clients B2B')} <ArrowRight aria-hidden="true" />
        </Link>
        {step.status === 'pending' && (
          <button type="button" className="u-btn u-btn--ghost" disabled={actions.busy} onClick={() => actions.decide('clients', 'none')}>
            {t('onboarding.clients.none', 'Je n’ai pas de clients B2B à déclarer')}
          </button>
        )}
      </Actions>
    </>
  );
};

// --- 3b/4. Carriers --------------------------------------------------------------------

const CarriersStep = ({ step, actions, t }) => {
  const f = step.facts || {};
  const [catalog, setCatalog] = useState([]);
  const [existing, setExisting] = useState([]);
  const [picked, setPicked] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get('/api/logistics/carrier-catalog', authHeaders()).catch(() => ({ data: [] })),
      axios.get('/api/logistics/carriers', authHeaders()).catch(() => ({ data: [] })),
    ]).then(([cat, mine]) => {
      if (cancelled) return;
      setCatalog(Array.isArray(cat.data) ? cat.data : []);
      setExisting(Array.isArray(mine.data) ? mine.data.map((c) => (c.name || '').trim().toLowerCase()) : []);
    });
    return () => { cancelled = true; };
  }, [f.total]);

  const toggle = (entry) => setPicked((prev) => {
    const next = { ...prev };
    if (next[entry.name]) delete next[entry.name];
    else next[entry.name] = { entry, email: '' };
    return next;
  });

  const chosen = Object.values(picked);
  const add = async () => {
    const missing = chosen.find(({ entry, email }) => entry.integration_mode === 'mail' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
    if (missing) return setError(t('onboarding.carriers.emailRequired', 'Indiquez l’adresse e-mail de contact de {{name}}.', { name: missing.entry.name }));
    setSaving(true);
    setError('');
    const failed = [];
    for (const { entry, email } of chosen) {
      const isWeb = entry.integration_mode === 'web_automation';
      try {
        // Same payload as « Ajouter un transporteur » on the carriers page.
        await axios.post('/api/logistics/carriers', {
          name: entry.name, emails: isWeb ? [] : [email.trim()], contact_rules: '', message_instructions: null,
          automation: false, is_mail: !isWeb, cc_emails: [],
        }, authHeaders());
      } catch (e) {
        failed.push(entry.name);
      }
    }
    setSaving(false);
    setPicked({});
    if (failed.length) setError(t('onboarding.carriers.saveError', 'Non ajouté(s) : {{names}}.', { names: failed.join(', ') }));
    await actions.refresh();
    return null;
  };

  return (
    <>
      {f.total > 0 && (
        <p className="onboarding-v2__ok">
          <CircleCheck aria-hidden="true" />
          {t('onboarding.carriers.count', '{{n}} transporteur(s) configuré(s).', { n: f.total })}
        </p>
      )}
      {(f.missing_credentials || []).length > 0 && (
        <Banner
          tone="warning"
          title={t('teamStatsUi.onboarding.missingCredentials', 'Sans compte portail : {{names}}', { names: f.missing_credentials.join(', ') })}
          actions={(
            <Link className="u-btn u-btn--secondary u-btn--sm" to="/carriers-instructions?flag=missing_credentials">
              {t('teamStatsUi.onboarding.addAccounts', 'Renseigner les comptes')}
            </Link>
          )}
        >
          {t('teamStatsUi.onboarding.missingCredentialsHint', 'L’agent ne peut pas déposer de demande sur leur portail tant que votre compte n’y est pas renseigné.')}
        </Banner>
      )}
      <ul className="onboarding-v2__carriers">
        {catalog.map((entry) => {
          const added = existing.includes(entry.name.trim().toLowerCase());
          const on = added || Boolean(picked[entry.name]);
          return (
            <li key={entry.name}>
              <label className={`onboarding-v2__carrier${on ? ' is-on' : ''}${added ? ' is-added' : ''}`}>
                <input type="checkbox" checked={on} disabled={added || saving} onChange={() => toggle(entry)} />
                <span className="onboarding-v2__carrier-text">
                  <span className="onboarding-v2__carrier-name">{entry.name}</span>
                  <span className="onboarding-v2__carrier-mode">{added
                    ? t('onboarding.carriers.added', 'ajouté')
                    : entry.integration_mode === 'web_automation'
                      ? t('onboarding.carriers.portal', 'par son portail')
                      : t('onboarding.carriers.mail', 'par e-mail')}</span>
                </span>
              </label>
              {picked[entry.name] && entry.integration_mode === 'mail' && (
                <input
                  className="u-input onboarding-v2__carrier-email"
                  type="email"
                  value={picked[entry.name].email}
                  aria-label={t('onboarding.carriers.emailLabel', 'Adresse e-mail de contact de {{name}}', { name: entry.name })}
                  placeholder={t('onboarding.carriers.emailPlaceholder', 'adresse de contact du transporteur')}
                  onChange={(e) => setPicked((prev) => ({ ...prev, [entry.name]: { ...prev[entry.name], email: e.target.value } }))}
                />
              )}
            </li>
          );
        })}
      </ul>
      <Hint>{t('onboarding.carriers.hint', 'Portail : l’agent dépose les demandes sur le site du transporteur ; vos identifiants se renseignent ensuite sur sa page. E-mail : il lui écrit à l’adresse indiquée.')}</Hint>
      {error && <Banner tone="danger">{error}</Banner>}
      <Actions>
        <button type="button" className="u-btn u-btn--primary" disabled={!chosen.length || saving || actions.busy} onClick={add}>
          <Plus aria-hidden="true" /> {chosen.length
            ? t('onboarding.carriers.addN', 'Ajouter {{n}} transporteur(s)', { n: chosen.length })
            : t('onboarding.carriers.pick', 'Cochez vos transporteurs')}
        </button>
        <Link className="u-btn u-btn--ghost" to="/carriers-instructions">
          {t('onboarding.carriers.other', 'Un transporteur manque ? Le demander')} <ArrowRight aria-hidden="true" />
        </Link>
      </Actions>
    </>
  );
};

// --- 4/5. Data sources (logistician) and shop (merchant) -------------------------------

const SOURCE_CARDS = [
  { key: 'orders', icon: ShoppingCart, title: 'Commandes — ERP, OMS', text: 'La commande, ses produits et ses colis.' },
  { key: 'warehouse', icon: Boxes, title: 'Entrepôt — WMS', text: 'Préparation, stock et expédition.' },
  { key: 'transport', icon: Truck, title: 'Transport — TMS, API transporteur', text: 'Le parcours du colis, si vous avez votre propre outil.' },
];

const SourcesStep = ({ step, actions, t }) => {
  const byCategory = step.facts?.by_category || {};
  return (
    <>
      <Rows>
        {SOURCE_CARDS.map(({ key, icon, title, text }) => (
          <Tile
            key={key} t={t} icon={icon} title={t(`onboarding.sources.${key}.title`, title)}
            detail={byCategory[key] ? t('onboarding.sources.count', '{{n}} source(s) connectée(s)', { n: byCategory[key] }) : t(`onboarding.sources.${key}.text`, text)}
            on={byCategory[key] > 0} route={`/connectors?view=choose&category=${key}`} actionText={byCategory[key] ? t('onboarding.sources.addAnother', 'En ajouter') : null}
          />
        ))}
      </Rows>
      <Hint>{t('onboarding.sources.hint', 'Le suivi des colis (Ship24) est inclus : rien à connecter. Une API se connecte en collant un exemple de réponse ; un site web en montrant une fois comment vous trouvez une commande.')}</Hint>
      <Actions>
        <Link className="u-btn u-btn--ghost" to="/connectors">
          <Database aria-hidden="true" /> {t('onboarding.sources.journey', 'Voir à quelle étape chaque source intervient')}
        </Link>
        {step.status === 'pending' && (
          <button type="button" className="u-btn u-btn--ghost" disabled={actions.busy} onClick={() => actions.decide('data_sources', 'none')}>
            {t('onboarding.sources.none', 'Je n’ai pas de système à connecter')}
          </button>
        )}
      </Actions>
    </>
  );
};

const ShopStep = ({ step, actions, t }) => {
  const f = step.facts || {};
  return (
    <>
      <Rows>
        <Tile
          t={t} icon={Store} title={t('onboarding.shop.shops', 'Shopify, PrestaShop')}
          detail={f.shops ? t('onboarding.shop.shopCount', '{{n}} boutique(s) connectée(s)', { n: f.shops }) : t('onboarding.shop.shopsHint', 'Les commandes, leurs colis et le suivi, écrits aussi sur la commande de la boutique.')}
          on={f.shops > 0} route="/shops"
        />
        <Tile
          t={t} icon={Plug} title={t('onboarding.shop.api', 'API commandes (autre plateforme)')}
          detail={f.api_keys ? t('onboarding.shop.keyCount', '{{n}} clé(s) API', { n: f.api_keys }) : t('onboarding.shop.apiHint', 'Votre site envoie ses commandes par une API simple.')}
          on={f.api_keys > 0} route="/commerce/orders?tab=api"
        />
        <Tile
          t={t} icon={Boxes} title={t('onboarding.shop.other', 'Logistique — WMS, ERP')}
          detail={f.other_sources ? t('onboarding.sources.count', '{{n}} source(s) connectée(s)', { n: f.other_sources }) : t('onboarding.shop.otherHint', 'Si un logisticien ou un outil prépare vos colis.')}
          on={f.other_sources > 0} route="/connectors?view=choose" actionText={t('onboarding.connect', 'Connecter')}
        />
      </Rows>
      {step.status === 'pending' && (
        <Actions>
          <button type="button" className="u-btn u-btn--ghost" disabled={actions.busy} onClick={() => actions.decide('shop', 'none')}>
            {t('onboarding.shop.none', 'Mes commandes ne sont dans aucun de ces outils')}
          </button>
        </Actions>
      )}
    </>
  );
};

// --- Agent -----------------------------------------------------------------------------

const AgentStep = ({ step, status, actions, t }) => {
  const f = step.facts || {};
  if (!f.running) {
    return (
      <>
        <Banner tone="warning">{t('onboarding.agent.none', 'Aucun agent actif : les tickets n’auront pas de réponse IA.')}</Banner>
        <Actions>
          <Link className="u-btn u-btn--primary" to="/my-ai-assistants">{t('onboarding.agent.create', 'Créer un agent')}</Link>
        </Actions>
      </>
    );
  }
  return (
    <>
      <Rows>
        <li className="u-row">
          <IconChip icon={Bot} />
          <div className="u-row__main">
            <div className="u-row__title">{f.name}</div>
            <div className="u-row__meta u-row__meta--wrap">{f.tools
              ? t('onboarding.agent.tools', '{{n}} outil(s) : suivi des colis, contact transporteur…', { n: f.tools })
              : t('onboarding.agent.noTools', 'Aucun outil : il ne pourra ni suivre un colis ni contacter un transporteur.')}</div>
          </div>
          <div className="u-row__side">
            <Link className="u-btn u-btn--secondary u-btn--sm" to={f.id ? `/ai-assistants?assistantId=${f.id}` : '/my-ai-assistants'}>
              {t('onboarding.agent.open', 'Ouvrir l’agent')} <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </li>
      </Rows>
      <ul className="onboarding-v2__checklist">
        <li><Check aria-hidden="true" />{t('onboarding.agent.check1', 'Son nom et sa signature sont ceux que vos clients doivent voir.')}</li>
        <li><Check aria-hidden="true" />{t('onboarding.agent.check2', 'Le ton (vouvoiement, longueur) vous ressemble.')}</li>
        <li><Check aria-hidden="true" />{t('onboarding.agent.check3', 'Il sait ce qu’il peut proposer : remboursement, renvoi, délai.')}</li>
      </ul>
      <Actions>
        {step.status !== 'done' && (
          <button type="button" className="u-btn u-btn--primary" disabled={actions.busy} onClick={() => actions.decide('agent', 'done')}>
            <Check aria-hidden="true" /> {t('onboarding.agent.confirm', 'J’ai vérifié mon agent')}
          </button>
        )}
        {status.demo_ticket_id && (
          <Link className="u-btn u-btn--ghost" to={`/tickets/${status.demo_ticket_id}/messages`}>
            {t('onboarding.agent.demo', 'Le voir répondre sur un ticket de démonstration')}
          </Link>
        )}
      </Actions>
    </>
  );
};

// --- Go live ---------------------------------------------------------------------------

const MODES = [
  { key: 'drafts', title: 'Brouillons à valider', text: 'L’agent prépare chaque réponse ; vous la relisez et l’envoyez. Recommandé les premiers jours.', badge: 'Recommandé' },
  { key: 'rules', title: 'Réponses automatiques sur certains cas', text: 'Vous choisissez les cas où la réponse part seule (ex. « où est mon colis ? ») ; le reste reste en brouillon.' },
];

const GoLiveStep = ({ step, actions, t }) => {
  const f = step.facts || {};
  const [mode, setMode] = useState(f.mode || 'drafts');
  return (
    <>
      <div className="onboarding-v2__choices" role="radiogroup" aria-label={t('onboarding.steps.go_live.title', 'Mise en service')}>
        {MODES.map(({ key, title, text, badge }) => (
          <Choice
            key={key}
            compact
            chosen={mode === key}
            onClick={() => setMode(key)}
            title={t(`onboarding.goLive.${key}.title`, title)}
            text={t(`onboarding.goLive.${key}.text`, text)}
            badge={badge ? <Badge tone="accent">{t('onboarding.goLive.recommended', badge)}</Badge> : null}
          />
        ))}
      </div>
      {mode === 'rules' && (
        <Hint>
          {f.automation_rules
            ? t('onboarding.goLive.rulesCount', '{{n}} règle(s) active(s).', { n: f.automation_rules })
            : t('onboarding.goLive.noRules', 'Aucune règle pour l’instant : tout reste en brouillon tant que vous n’en créez pas.')}{' '}
          <Link className="u-link" to="/ai-automation?tab=rules">{t('onboarding.goLive.openRules', 'Choisir les cas')} <ArrowRight className="onboarding-v2__inline-icon" aria-hidden="true" /></Link>
        </Hint>
      )}
      <ul className="onboarding-v2__checklist onboarding-v2__checklist--state">
        <li className={f.identify_client ? 'is-on' : ''}>
          {f.identify_client ? <CircleCheck aria-hidden="true" /> : <CircleDashed aria-hidden="true" />}
          {f.identify_client
            ? t('onboarding.goLive.identifyOn', 'Le client est reconnu à l’arrivée de chaque ticket.')
            : t('onboarding.goLive.identifyOff', 'Le client n’est reconnu que sur certains canaux.')}
        </li>
        <li className={f.read_sources ? 'is-on' : ''}>
          {f.read_sources ? <CircleCheck aria-hidden="true" /> : <CircleDashed aria-hidden="true" />}
          {f.read_sources
            ? t('onboarding.goLive.sourcesOn', 'Vos sources sont lues à l’arrivée de chaque ticket.')
            : t('onboarding.goLive.sourcesOff', 'Vos sources ne sont lues que si l’agent les interroge.')}
        </li>
      </ul>
      <Actions>
        <button
          type="button"
          className="u-btn u-btn--primary"
          disabled={actions.busy || (step.status === 'done' && f.mode === mode)}
          onClick={() => actions.decide('go_live', 'done', mode)}
        >
          <Check aria-hidden="true" /> {step.status === 'done'
            ? t('onboarding.goLive.update', 'Enregistrer ce choix')
            : t('onboarding.goLive.confirm', 'Mettre en service')}
        </button>
        {(!f.identify_client || !f.read_sources) && (
          <Link className="u-btn u-btn--ghost" to="/connectors?step=identification">
            {t('onboarding.goLive.reception', 'Activer la réception complète')} <ArrowRight aria-hidden="true" />
          </Link>
        )}
      </Actions>
    </>
  );
};

const PANELS = {
  profile: ProfileStep,
  channels: ChannelsStep,
  clients: ClientsStep,
  carriers: CarriersStep,
  data_sources: SourcesStep,
  shop: ShopStep,
  agent: AgentStep,
  go_live: GoLiveStep,
};

export const StepPanel = ({ step, status, actions }) => {
  const { t } = useTranslation();
  const Panel = PANELS[step.key];
  if (!Panel) return null;
  return <div className="onboarding-v2__panel-body"><Panel step={step} status={status} actions={actions} t={t} /></div>;
};
